import {
  CommercialAssumptions,
  CommercialContent,
  CommercialContentWire,
  CommercialCostSummary,
  CommercialHeaderAtom,
  CommercialLabour,
  CommercialSectionMarkdown,
  CommercialWhyNeeded,
  CreateQuoteDocumentVersionResponseWire,
  CreateQuoteDocumentVersionResult,
  ListQuoteDocumentsParams,
  NumberLike,
  PricingStatus,
  QuoteDocument,
  QuoteDocumentActivity,
  QuoteDocumentActivityDetails,
  QuoteDocumentActivityListResponse,
  QuoteDocumentActivityListResponseWire,
  QuoteDocumentActivityWire,
  QuoteDocumentCapabilities,
  QuoteDocumentCapabilityFlagsWire,
  QuoteDocumentDetail,
  QuoteDocumentDetailResponseWire,
  QuoteDocumentNteStatus,
  QuoteDocumentRevision,
  QuoteDocumentRevisionsListResponse,
  QuoteDocumentRevisionsListResponseWire,
  QuoteDocumentRevisionWire,
  QuoteDocumentsListResponse,
  QuoteDocumentsListResponseWire,
  QuoteDocumentStatus,
  QuoteDocumentWire,
  QuoteLine,
  QuoteLineWire,
} from '@/types/quoteDocuments';

/** Keys that must never surface from activity details (full SoR payloads). */
const ACTIVITY_DETAILS_BLOCKLIST = new Set([
  'commercial_content',
  'commercialContent',
  'lines',
  'narrative',
  'snapshot',
]);

const PRICING_STATUSES: PricingStatus[] = [
  'priced',
  'poa',
  'tbc',
  'indicative',
];

const NTE_STATUSES: QuoteDocumentNteStatus[] = ['under', 'over', 'unknown'];

const HEAD_STATUSES: QuoteDocumentStatus[] = [
  'draft',
  'approved',
  'accepted',
  'declined',
];

export const normalizeNteStatus = (value: unknown): QuoteDocumentNteStatus => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase() as QuoteDocumentNteStatus;
    if (NTE_STATUSES.includes(lower)) return lower;
  }
  return 'unknown';
};

/** Head `issued` is a legacy alias for approved. Revisions keep `issued`. */
export const normalizeQuoteDocumentHeadStatus = (
  value: unknown
): QuoteDocumentStatus | string | null => {
  if (value == null || value === '') return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'issued') return 'approved';
  if (HEAD_STATUSES.includes(raw as QuoteDocumentStatus)) {
    return raw as QuoteDocumentStatus;
  }
  return raw;
};

export const parseNumberLike = (
  value: NumberLike | null | undefined
): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Parse a quote money string such as `£2,624.39`. Placeholders like `£POA` stay empty. */
export const parseMoneyLike = (value: unknown): number | null => {
  const direct = parseNumberLike(
    typeof value === 'number' || typeof value === 'string' ? value : null
  );
  if (direct != null) return direct;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || !/\d/.test(trimmed)) return null;
  const match = trimmed.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toQuoteDocumentDate = (
  value: number | string | null | undefined
): Date | null => {
  if (value == null || value === '' || value === 0 || value === '0')
    return null;
  if (typeof value === 'number') {
    const ms = value < 1_000_000_000_000 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizePricingStatus = (value: unknown): PricingStatus => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase() as PricingStatus;
    if (PRICING_STATUSES.includes(lower)) return lower;
  }
  return 'poa';
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Live commercial prose may arrive as string, number, or richer JSON — never crash trim. */
const asTrimmedString = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const joined = value
      .map(item => asTrimmedString(item))
      .filter((item): item is string => Boolean(item))
      .join('\n');
    return joined || null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['text', 'value', 'content', 'body', 'markdown']) {
      const nested = asTrimmedString(record[key]);
      if (nested) return nested;
    }
    if (Array.isArray(record.paragraphs)) {
      return asTrimmedString(record.paragraphs);
    }
  }
  return null;
};

const asStringList = (items: unknown): string[] | null => {
  if (!Array.isArray(items)) return null;
  const list = items
    .map(item => asTrimmedString(item))
    .filter((item): item is string => Boolean(item));
  return list.length > 0 ? list : null;
};

const compactSectionMarkdown = (
  sections: CommercialSectionMarkdown
): CommercialSectionMarkdown | null => {
  const compact: CommercialSectionMarkdown = {};
  (Object.keys(sections) as Array<keyof CommercialSectionMarkdown>).forEach(
    key => {
      const value = sections[key];
      if (value) compact[key] = value;
    }
  );
  return Object.keys(compact).length > 0 ? compact : null;
};

const normalizeHeader = (
  header: unknown
): { atoms: CommercialHeaderAtom[] | null; markdown: string | null } => {
  if (header == null || header === '') {
    return { atoms: null, markdown: null };
  }
  if (typeof header === 'string') {
    return { atoms: null, markdown: asTrimmedString(header) };
  }
  if (Array.isArray(header)) {
    const atoms = header.filter(isPlainRecord) as CommercialHeaderAtom[];
    return { atoms: atoms.length > 0 ? atoms : null, markdown: null };
  }
  if (isPlainRecord(header)) {
    const atoms = Object.entries(header).map(([label, value]) => ({
      label,
      value: value == null ? '' : String(value),
    }));
    return { atoms: atoms.length > 0 ? atoms : null, markdown: null };
  }
  return { atoms: null, markdown: null };
};

const normalizeRecommendedWorks = (
  value: unknown
): { items: string[]; markdown: string | null } => {
  if (!value) return { items: [], markdown: null };
  if (Array.isArray(value)) {
    const items = value
      .map(item => asTrimmedString(item) ?? String(item))
      .map(item => item.trim())
      .filter(Boolean);
    return { items, markdown: null };
  }
  return { items: [], markdown: asTrimmedString(value) };
};

const normalizeWhyNeeded = (
  value: unknown
): { structured: CommercialWhyNeeded | null; markdown: string | null } => {
  if (typeof value === 'string') {
    return { structured: null, markdown: asTrimmedString(value) };
  }
  if (!isPlainRecord(value)) return { structured: null, markdown: null };
  const root_cause = asTrimmedString(value.root_cause);
  const consequences = asTrimmedString(value.consequences);
  const approach = asTrimmedString(value.approach);
  if (!root_cause && !consequences && !approach) {
    return { structured: null, markdown: null };
  }
  return {
    structured: { root_cause, consequences, approach },
    markdown: null,
  };
};

const normalizeLabour = (value: unknown): CommercialLabour | null => {
  if (typeof value === 'string') {
    const display = asTrimmedString(value);
    return display ? { display } : null;
  }
  if (!isPlainRecord(value)) return null;
  const display = asTrimmedString(value.display);
  const hours = value.hours;
  const rate = value.rate;
  const total = value.total;
  if (
    !display &&
    hours == null &&
    rate == null &&
    total == null
  ) {
    return null;
  }
  return {
    display,
    hours:
      typeof hours === 'number' || typeof hours === 'string' ? hours : null,
    rate: typeof rate === 'number' || typeof rate === 'string' ? rate : null,
    total:
      typeof total === 'number' || typeof total === 'string' ? total : null,
  };
};

const normalizeCostSummary = (
  value: unknown
): { structured: CommercialCostSummary | null; markdown: string | null } => {
  if (typeof value === 'string') {
    return { structured: null, markdown: asTrimmedString(value) };
  }
  if (!isPlainRecord(value)) return { structured: null, markdown: null };
  const materials = asTrimmedString(value.materials);
  const labour = asTrimmedString(value.labour);
  const vat = asTrimmedString(value.vat);
  const total =
    asTrimmedString(value.total) ||
    asTrimmedString(value.total_quoted_price) ||
    asTrimmedString(value.subtotal_excl_vat);
  const validUntil = asTrimmedString(value.price_valid_until);
  if (!materials && !labour && !vat && !total && !validUntil) {
    return { structured: null, markdown: null };
  }
  return {
    structured: { materials, labour, vat, total, validUntil },
    markdown: null,
  };
};

const normalizeAssumptions = (
  value: unknown
): { structured: CommercialAssumptions | null; markdown: string | null } => {
  if (typeof value === 'string') {
    return { structured: null, markdown: asTrimmedString(value) };
  }
  if (!isPlainRecord(value)) return { structured: null, markdown: null };
  const standard = asStringList(value.standard);
  const specific = asStringList(value.specific);
  const exclusions = asStringList(value.exclusions);
  const terms = asTrimmedString(value.terms);
  if (!standard && !specific && !exclusions && !terms) {
    return { structured: null, markdown: null };
  }
  return {
    structured: { standard, specific, exclusions, terms },
    markdown: null,
  };
};

export const normalizeCommercialContent = (
  wire: CommercialContentWire | null | undefined
): CommercialContent | null => {
  if (!wire) return null;
  const header = normalizeHeader(wire.header);
  const recommendedWorks = normalizeRecommendedWorks(wire.recommended_works);
  const whyNeeded = normalizeWhyNeeded(wire.why_needed);
  const costSummary = normalizeCostSummary(wire.cost_summary);
  const assumptions = normalizeAssumptions(wire.assumptions);

  return {
    headline: asTrimmedString(wire.headline),
    executiveSummary: asTrimmedString(wire.executive_summary),
    header: header.atoms,
    background: asTrimmedString(wire.background),
    recommendedWorks: recommendedWorks.items,
    whyNeeded: whyNeeded.structured,
    parts: wire.parts ?? null,
    partsNotes: asTrimmedString(wire.parts_notes),
    labour: normalizeLabour(wire.labour),
    costSummary: costSummary.structured,
    assumptions: assumptions.structured,
    markdown: compactSectionMarkdown({
      ...(header.markdown ? { header: header.markdown } : {}),
      ...(recommendedWorks.markdown
        ? { recommendedWorks: recommendedWorks.markdown }
        : {}),
      ...(whyNeeded.markdown ? { whyNeeded: whyNeeded.markdown } : {}),
      ...(costSummary.markdown ? { costSummary: costSummary.markdown } : {}),
      ...(assumptions.markdown ? { assumptions: assumptions.markdown } : {}),
    }),
  };
};

export const normalizeQuoteLine = (wire: QuoteLineWire): QuoteLine => {
  const quantity = parseNumberLike(wire.quantity);
  const unitSell = parseNumberLike(wire.unitSell);
  const pricingStatus = normalizePricingStatus(wire.pricingStatus);
  const lineTotal =
    pricingStatus === 'priced' && quantity != null && unitSell != null
      ? quantity * unitSell
      : null;

  return {
    kind: wire.kind,
    quantity,
    uom: wire.uom?.trim() || null,
    unitCost: parseNumberLike(wire.unitCost),
    unitSell,
    taxPercentage: parseNumberLike(wire.taxPercentage),
    description: wire.description?.trim() || '',
    catalogRef: wire.catalogRef?.trim() || null,
    pricingStatus,
    audit: wire.audit ?? null,
    lineTotal,
  };
};

const displayTitleFor = (
  title: string | null | undefined,
  commercial: CommercialContent | null
): string => {
  const trimmedTitle = title?.trim();
  if (trimmedTitle) return trimmedTitle;
  if (commercial?.headline) return commercial.headline;
  return 'Untitled Quote Document';
};

export const normalizeQuoteDocument = (
  wire: QuoteDocumentWire
): QuoteDocument => {
  const commercialContent = normalizeCommercialContent(wire.commercial_content);
  const title = wire.title?.trim() || null;

  return {
    id: wire.id,
    createdAt: toQuoteDocumentDate(wire.created_at),
    updatedAt: toQuoteDocumentDate(wire.updated_at),
    companyId: wire.company_id ?? null,
    runId: wire.run_id ?? null,
    lastRunId: wire.last_run_id ?? null,
    documentType: wire.document_type ?? null,
    status: normalizeQuoteDocumentHeadStatus(wire.status),
    runStatus: wire.run_status ?? null,
    customerRef: wire.customer_ref?.trim() || null,
    siteRef: wire.site_ref?.trim() || null,
    jobRef: wire.job_ref?.trim() || null,
    jobGroupId: wire.job_group_id?.trim() || null,
    originResourceId: wire.origin_resource_id?.trim() || null,
    originResourceName: wire.origin_resource_name?.trim() || null,
    originResourceEmail: wire.origin_resource_email?.trim() || null,
    documentGroupId: wire.document_group_id ?? null,
    version: typeof wire.version === 'number' ? wire.version : 0,
    acceptedVersionId: wire.accepted_version_id ?? null,
    title,
    currency: wire.currency?.trim() || 'GBP',
    vatPercentage: parseNumberLike(wire.vat_percentage),
    validUntil: toQuoteDocumentDate(wire.valid_until),
    provenance: wire.provenance ?? null,
    lines: Array.isArray(wire.lines) ? wire.lines.map(normalizeQuoteLine) : [],
    errorReason: wire.error_reason?.trim() || null,
    commercialContent,
    draftRevision:
      typeof wire.draft_revision === 'number' ? wire.draft_revision : 0,
    hasUnissuedChanges: Boolean(wire.has_unissued_changes),
    assignedUserId:
      typeof wire.assigned_user_id === 'number' ? wire.assigned_user_id : null,
    nteAmount: parseNumberLike(wire.nte_amount),
    nteStatus: normalizeNteStatus(wire.nte_status),
    archivedAt: toQuoteDocumentDate(wire.archived_at),
    displayTitle: displayTitleFor(title, commercialContent),
  };
};

const flagFrom = (
  wire: QuoteDocumentCapabilityFlagsWire,
  nested: QuoteDocumentCapabilityFlagsWire | undefined,
  key: keyof QuoteDocumentCapabilityFlagsWire
): boolean => Boolean(wire[key] ?? nested?.[key]);

const lifecycleFlag = (
  wire: QuoteDocumentCapabilityFlagsWire,
  nested: QuoteDocumentCapabilityFlagsWire | undefined,
  key: keyof QuoteDocumentCapabilityFlagsWire,
  inferred: boolean
): boolean => {
  const explicit = wire[key] ?? nested?.[key];
  if (explicit != null) return Boolean(explicit);
  return inferred;
};

const normalizeCapabilities = (
  wire: QuoteDocumentDetailResponseWire,
  status: QuoteDocumentStatus | string | null,
  archivedAt: Date | null
): QuoteDocumentCapabilities => {
  const nested = wire.capabilities;
  const isDraft = status === 'draft';
  const isApproved = status === 'approved';
  const isArchived = archivedAt != null;
  return {
    canEdit: flagFrom(wire, nested, 'can_edit') && !isArchived,
    canCreateVersion: flagFrom(wire, nested, 'can_create_version') && !isArchived,
    canChat: flagFrom(wire, nested, 'can_chat') && !isArchived,
    canApprove: lifecycleFlag(wire, nested, 'can_approve', isDraft) && !isArchived,
    canAccept:
      lifecycleFlag(wire, nested, 'can_accept', isApproved) && !isArchived,
    canDecline:
      lifecycleFlag(wire, nested, 'can_decline', isApproved) && !isArchived,
    canReopen:
      lifecycleFlag(
        wire,
        nested,
        'can_reopen',
        status === 'accepted' || status === 'declined' || status === 'expired'
      ) && !isArchived,
    canDuplicate: lifecycleFlag(wire, nested, 'can_duplicate', true) && !isArchived,
    canArchive: lifecycleFlag(wire, nested, 'can_archive', true) && !isArchived,
  };
};

export const normalizeQuoteDocumentDetail = (
  wire: QuoteDocumentDetailResponseWire | QuoteDocumentWire
): QuoteDocumentDetail => {
  const wrapped = wire as QuoteDocumentDetailResponseWire;
  const hasWrappedRecord =
    wrapped.record != null && typeof wrapped.record === 'object';
  const record: QuoteDocumentWire = hasWrappedRecord
    ? wrapped.record!
    : (wire as QuoteDocumentWire);
  const document = normalizeQuoteDocument(record);

  return {
    ...document,
    capabilities: normalizeCapabilities(
      hasWrappedRecord ? wrapped : { record },
      document.status,
      document.archivedAt
    ),
  };
};

export const normalizeQuoteDocumentsList = (
  wire: QuoteDocumentsListResponseWire
): QuoteDocumentsListResponse => {
  const items = (wire.items ?? []).map(normalizeQuoteDocument).sort((a, b) => {
    const aTime = a.createdAt?.getTime() ?? 0;
    const bTime = b.createdAt?.getTime() ?? 0;
    return bTime - aTime;
  });

  return {
    itemsReceived: wire.itemsReceived ?? items.length,
    curPage: wire.curPage ?? 1,
    nextPage: wire.nextPage ?? null,
    prevPage: wire.prevPage ?? null,
    offset: wire.offset ?? 0,
    perPage: wire.perPage ?? items.length,
    itemsTotal: wire.itemsTotal ?? items.length,
    pageTotal: wire.pageTotal ?? 1,
    items,
  };
};

const compactActivityDetails = (
  details: QuoteDocumentActivityDetails | null | undefined
): QuoteDocumentActivityDetails | null => {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return null;
  }

  const compact: QuoteDocumentActivityDetails = {};
  for (const [key, value] of Object.entries(details)) {
    if (ACTIVITY_DETAILS_BLOCKLIST.has(key)) continue;
    compact[key] = value;
  }
  return compact;
};

export const normalizeQuoteDocumentActivity = (
  wire: QuoteDocumentActivityWire
): QuoteDocumentActivity => ({
  id: wire.id,
  createdAt: toQuoteDocumentDate(wire.created_at),
  companyId: wire.company_id ?? null,
  documentId: wire.document_id ?? null,
  revisionId: wire.revision_id ?? null,
  actorType: wire.actor_type,
  actorId: wire.actor_id ?? null,
  action: wire.action,
  details: compactActivityDetails(wire.details),
  actorName: wire.actor_name?.trim() || null,
  actorAvatar: wire.actor_avatar?.trim() || null,
  initiatedByUserName: wire.initiated_by_user_name?.trim() || null,
});

export const normalizeQuoteDocumentActivityList = (
  wire: QuoteDocumentActivityListResponseWire | null | undefined
): QuoteDocumentActivityListResponse => {
  const items = (wire?.items ?? []).map(normalizeQuoteDocumentActivity);

  return {
    itemsReceived: wire?.itemsReceived ?? items.length,
    curPage: wire?.curPage ?? 1,
    nextPage: wire?.nextPage ?? null,
    prevPage: wire?.prevPage ?? null,
    offset: wire?.offset ?? 0,
    perPage: wire?.perPage ?? items.length,
    itemsTotal: wire?.itemsTotal ?? items.length,
    pageTotal: wire?.pageTotal ?? 1,
    items,
  };
};

export const normalizeQuoteDocumentRevision = (
  wire: QuoteDocumentRevisionWire
): QuoteDocumentRevision => {
  const commercialContent = normalizeCommercialContent(wire.commercial_content);
  const title = wire.title?.trim() || null;

  return {
    id: wire.id,
    createdAt: toQuoteDocumentDate(wire.created_at),
    companyId: wire.company_id ?? null,
    documentId: wire.document_id ?? null,
    documentGroupId: wire.document_group_id ?? null,
    version: typeof wire.version === 'number' ? wire.version : 0,
    status: wire.status,
    lines: Array.isArray(wire.lines) ? wire.lines.map(normalizeQuoteLine) : [],
    commercialContent,
    title,
    currency: wire.currency?.trim() || 'GBP',
    vatPercentage: parseNumberLike(wire.vat_percentage),
    validUntil: toQuoteDocumentDate(wire.valid_until),
    customerRef: wire.customer_ref?.trim() || null,
    siteRef: wire.site_ref?.trim() || null,
    jobRef: wire.job_ref?.trim() || null,
    jobGroupId: wire.job_group_id?.trim() || null,
    originResourceId: wire.origin_resource_id?.trim() || null,
    originResourceName: wire.origin_resource_name?.trim() || null,
    originResourceEmail: wire.origin_resource_email?.trim() || null,
    provenance: wire.provenance ?? null,
    changeSummary: wire.change_summary?.trim() || null,
    issuedAt: toQuoteDocumentDate(wire.issued_at),
    issuedBy: wire.issued_by?.trim() || null,
    sourceDraftRevision:
      typeof wire.source_draft_revision === 'number'
        ? wire.source_draft_revision
        : null,
    displayTitle: displayTitleFor(title, commercialContent),
  };
};

export const normalizeQuoteDocumentRevisionsList = (
  wire:
    | QuoteDocumentRevisionsListResponseWire
    | QuoteDocumentRevisionWire[]
    | null
    | undefined
): QuoteDocumentRevisionsListResponse => {
  const rawItems = Array.isArray(wire) ? wire : (wire?.items ?? []);
  const items = rawItems
    .map(normalizeQuoteDocumentRevision)
    .sort((a, b) => b.version - a.version);

  return { items };
};

export const normalizeCreateQuoteDocumentVersionResult = (
  wire: CreateQuoteDocumentVersionResponseWire
): CreateQuoteDocumentVersionResult => {
  if (!wire.version || !wire.record) {
    throw new Error('Create version response missing version or updated head');
  }

  return {
    version: normalizeQuoteDocumentRevision(wire.version),
    document: normalizeQuoteDocumentDetail({
      record: wire.record,
      can_edit: wire.can_edit,
      can_create_version: wire.can_create_version,
      can_chat: wire.can_chat,
      can_approve: wire.can_approve,
      can_accept: wire.can_accept,
      can_decline: wire.can_decline,
      can_reopen: wire.can_reopen,
      can_duplicate: wire.can_duplicate,
      can_archive: wire.can_archive,
    }),
  };
};

export const filterQuoteDocumentWires = (
  items: QuoteDocumentWire[],
  params: ListQuoteDocumentsParams = {}
): QuoteDocumentWire[] => {
  const search = params.search?.trim().toLowerCase();
  return items.filter(item => {
    if (params.runId) {
      const matchesRun =
        item.run_id === params.runId || item.last_run_id === params.runId;
      if (!matchesRun) return false;
    }
    if (params.status && item.status !== params.status) return false;
    if (params.runStatus && item.run_status !== params.runStatus) return false;
    if (params.jobRef && item.job_ref !== params.jobRef) return false;
    if (params.archived === true) {
      if (!item.archived_at) return false;
    } else if (item.archived_at) {
      return false;
    }
    if (params.jobGroupId && item.job_group_id !== params.jobGroupId) {
      return false;
    }
    if (search) {
      const haystack = [
        item.title,
        item.job_ref,
        item.customer_ref,
        item.site_ref,
        item.origin_resource_name,
        item.commercial_content?.headline,
        item.commercial_content?.executive_summary,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
};
